import React from 'react'
import { Landmark } from 'lucide-react'
import { moneyOrBlank, monthLabel } from './overview'
import { fmtDay } from '../financial/formatting'

// ─────────────────────────────────────────────────────────────────────────────
//   Deuda bancaria — un pasivo, no el gasto del mes
//
//   Va visualmente separada de los tres KPI de arriba porque responde a otra
//   pregunta. Aquellos son FLUJOS del mes; esto es una POSICIÓN a una fecha, y
//   por eso el backend le pone su propio `as_of` en vez de dejarla heredar el
//   rango. Mezclarlas es cómo "hemos amortizado $20.825" y "debemos $90.175"
//   acaban en la misma columna.
//
//   Y no es gasto: de un plazo pagado sólo el interés es un coste, y el
//   principal reduce un pasivo sin costar nada. El interés del mes vive en
//   `debt_service.interest`, dentro del consolidado; aquí sólo está el saldo.
// ─────────────────────────────────────────────────────────────────────────────

function DebtFigure({ label, value, hint, strong, tone }) {
  return (
    <div style={{ minWidth: 0 }}>
      <div style={{ fontSize: 11, color: 'var(--text-muted)', textTransform: 'uppercase', letterSpacing: '0.04em' }}>
        {label}
      </div>
      <div
        style={{
          fontSize: strong ? 22 : 18,
          fontWeight: strong ? 700 : 600,
          color: tone || 'var(--text-primary)',
          marginTop: 2,
        }}
      >
        {value}
      </div>
      {hint && (
        <div style={{ fontSize: 11.5, color: 'var(--text-muted)', marginTop: 2, lineHeight: 1.45 }}>{hint}</div>
      )}
    </div>
  )
}

export default function BankDebtPanel({ bankDebt, monthTo, loading }) {
  const loans = bankDebt?.loans || []

  return (
    <div
      className="card"
      style={{
        marginBottom: 16, background: 'var(--bg-tint)', borderStyle: 'dashed',
        // El único borde rojo de la pantalla, y marca la sección entera: todo
        // lo de esta tarjeta es un pasivo. El discontinuo se queda porque es lo
        // que ya la separaba de los KPI de flujo de arriba.
        borderLeft: '3px solid var(--red)',
      }}
    >
      <div style={{ display: 'flex', gap: 10, alignItems: 'flex-start', marginBottom: 14 }}>
        <div style={{ color: 'var(--text-secondary)', flexShrink: 0, marginTop: 1 }}>
          <Landmark size={17} />
        </div>
        <div style={{ minWidth: 0 }}>
          <h3 style={{ margin: 0, fontSize: 15 }}>Bank debt outstanding</h3>
          {/* Una línea. Lo que decía el párrafo largo —que un saldo no se suma
              entre meses, y que de un plazo sólo el interés es coste— lo dicen
              ya los `hint` de las tres cifras de debajo, cada uno pegado a la
              cifra de la que habla. La fecha de corte es del backend y no del
              rango: una posición y un flujo responden preguntas distintas. */}
          <p style={{ margin: '4px 0 0', fontSize: 12.5, color: 'var(--text-secondary)', lineHeight: 1.55 }}>
            A liability, not spend — a position{' '}
            <strong>as of {bankDebt?.as_of ? fmtDay(bankDebt.as_of) : `the end of ${monthLabel(monthTo)}`}</strong>,
            not a flow over the window.
          </p>
        </div>
      </div>

      <div
        className="cf-debt"
        style={{ display: 'grid', gridTemplateColumns: 'repeat(3, minmax(0, 1fr))', gap: 12 }}
      >
        <DebtFigure
          label="Original principal"
          value={loading ? '—' : moneyOrBlank(bankDebt?.original_principal)}
          hint="What was borrowed."
        />
        <DebtFigure
          label="Principal repaid"
          // Verde: es lo que efectivamente se movió contra la deuda.
          tone={loading ? undefined : 'var(--green)'}
          value={loading ? '—' : moneyOrBlank(bankDebt?.principal_repaid)}
          hint="Reduces the liability; never a cost. Only the interest is, and it is in the spend below."
        />
        <DebtFigure
          strong
          label="Outstanding"
          // Rojo: es lo que falta. Es el mismo significado que tiene el rojo en
          // Accounts receivable y en Accounts payable, no una alarma.
          tone={loading ? undefined : 'var(--red)'}
          value={loading ? '—' : moneyOrBlank(bankDebt?.outstanding)}
          hint="Still owed at that date. A balance, never a total across months."
        />
      </div>

      {/* Un préstamo por tarjeta y las tarjetas en rejilla, no una lista de
          filas: cada préstamo son tres datos cortos —quién, en qué estado y
          cuánto queda— y en una fila a todo lo ancho eso deja el 70% derecho
          vacío y cuesta una línea de scroll por préstamo.

          `auto-fit` y no tres columnas fijas: cuántos préstamos hay es un dato,
          y con uno solo una rejilla fija dejaría una tarjeta suelta con dos
          huecos al lado. */}
      {!loading && loans.length > 0 && (
        <div
          className="cf-loans"
          style={{
            marginTop: 14, borderTop: '1px solid var(--border)', paddingTop: 14,
            display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(260px, 1fr))', gap: 10,
          }}
        >
          {loans.map((loan) => (
            <div
              key={loan.loan_id}
              style={{
                display: 'flex', flexDirection: 'column', gap: 6, minWidth: 0,
                padding: '10px 12px', border: '1px solid var(--border)',
                borderRadius: 'var(--radius)', background: 'var(--bg-raised)',
              }}
            >
              <div style={{ minWidth: 0, display: 'flex', gap: 8, alignItems: 'baseline', flexWrap: 'wrap' }}>
                <span style={{ fontSize: 13, fontWeight: 600, color: 'var(--text-primary)' }}>
                  {loan.lender_name}
                </span>
                <span className="badge badge-gray" style={{ fontSize: 10 }}>{loan.status}</span>
              </div>
              {/* `payment_is_estimated` está en la tarjeta porque los dos casos
                  se ven idénticos como dólares: mientras el plazo se amortiza
                  en vez de leerse de un extracto, la cifra es un cálculo
                  nuestro y no un dato del banco. */}
              {loan.payment_is_estimated && (
                <span
                  className="badge badge-amber"
                  style={{ fontSize: 10, alignSelf: 'flex-start' }}
                  title="No bank statement has been seen: the instalment is amortised from principal, rate and term"
                >
                  instalment estimated
                </span>
              )}
              {/* `marginTop: auto` mantiene los importes alineados abajo aunque
                  una tarjeta lleve la insignia de plazo estimado y su vecina
                  no. Sin eso, la rejilla pone cada cifra a una altura. */}
              <span
                style={{
                  marginTop: 'auto', fontSize: 16, fontWeight: 700,
                  color: 'var(--red)', whiteSpace: 'nowrap',
                }}
              >
                {moneyOrBlank(loan.outstanding)}
              </span>
            </div>
          ))}
        </div>
      )}
    </div>
  )
}
